'use client';

import { DropdownMenu } from 'radix-ui';
import type { ReactNode } from 'react';
import { cn } from './cn';
import { Kbd } from './kbd';
import { panelSurfaceClass } from './popover';

/** A menu's panel: the shared anchored surface (`panelSurfaceClass`) at elevation 1. */
export const menuContentClass = cn(
  panelSurfaceClass,
  'min-w-48 rounded-crow-panel p-1 shadow-crow-1',
);

/**
 * A menu's row: highlighted in the raised colour (a danger row in the danger tint), its icon in
 * secondary text (danger in a danger row) at 16 px unless the icon sets its own colour or size. Radix's checkbox and radio
 * items add their indicator's room (`pl-8`) to it.
 */
export function menuItemClass(danger = false) {
  return cn(
    "relative flex cursor-default items-center gap-2.5 rounded-[5px] px-2 py-1.5 text-crow-compact outline-none select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
    danger
      ? "text-crow-danger data-[highlighted]:bg-crow-danger-tint [&_svg:not([class*='text-'])]:text-crow-danger"
      : "data-[highlighted]:bg-crow-raised [&_svg:not([class*='text-'])]:text-crow-text-2",
  );
}

/** A menu's group heading. */
export const menuLabelClass = 'px-2 pt-2 pb-1 text-crow-label text-crow-text-3';

/** The line between a menu's groups. */
export const menuSeparatorClass = '-mx-1 my-1 h-px bg-crow-line';

/** A menu opened from a button: elevation 1, it grows from its trigger. */
export function Menu({
  trigger,
  children,
  align = 'end',
  side = 'bottom',
  className,
}: {
  /** The element that opens the menu, usually a Button or an IconButton. */
  trigger: ReactNode;
  children: ReactNode;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'bottom';
  className?: string;
}) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align={align}
          side={side}
          sideOffset={4}
          className={cn(menuContentClass, className)}
        >
          {children}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export function MenuItem({
  onSelect,
  icon,
  shortcut,
  hint,
  danger = false,
  disabled = false,
  children,
}: {
  onSelect: () => void;
  icon?: ReactNode;
  shortcut?: string;
  /** Short text on the right: an address, a time, a count. */
  hint?: ReactNode;
  danger?: boolean;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <DropdownMenu.Item onSelect={onSelect} disabled={disabled} className={menuItemClass(danger)}>
      {icon}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {hint ? <span className="shrink-0 text-crow-label text-crow-text-3">{hint}</span> : null}
      {shortcut ? <Kbd>{shortcut}</Kbd> : null}
    </DropdownMenu.Item>
  );
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <DropdownMenu.Label className={menuLabelClass}>{children}</DropdownMenu.Label>;
}

export function MenuSeparator() {
  return <DropdownMenu.Separator className={menuSeparatorClass} />;
}
